import { useEffect, useState } from "react";
import { FolderPlus } from "lucide-react";
import "./collection-picker.css";
import { useApiService } from "../../services/apiService";
import { useSharedPermissions } from "../../contexts/PermissionsContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { CollectionEditor } from "./CollectionEditor";
import {
  addToCollections,
  collectionPath,
  productEntityId,
  type Collection,
} from "./collections";
export function AddToCollections({
  entityId,
  entityIds,
  name,
  iconOnly = false,
}: {
  entityId?: unknown;
  entityIds?: number[];
  name: string;
  iconOnly?: boolean;
}) {
  const permissions = useSharedPermissions();
  const ids = [
    ...new Set(
      (entityIds ?? [entityId])
        .map(productEntityId)
        .filter((id): id is number => id !== null)
    ),
  ];
  const id = ids[0];
  const [open, setOpen] = useState(false);
  if (
    !permissions.canReadCollections ||
    !permissions.canAddCollectionMemberships
  )
    return null;
  return (
    <>
      <button
        type="button"
        className={`ps-action-secondary${iconOnly ? " ps-action-icon" : ""}`}
        aria-label="Add to collections"
        disabled={!id}
        title={!id ? "Product identifier is unavailable" : iconOnly ? "Add to collections" : undefined}
        onClick={() => setOpen(true)}
      >
        {iconOnly ? <FolderPlus aria-hidden="true" /> : "Add to collections"}
      </button>
      {open && id && (
        <CollectionPicker
          entityIds={ids}
          name={name}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
function CollectionPicker({
  entityIds,
  name,
  onClose,
}: {
  entityIds: number[];
  name: string;
  onClose: () => void;
}) {
  const api = useApiService();
  const permissions = useSharedPermissions();
  const [items, setItems] = useState<Collection[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [done, setDone] = useState<string[]>([]);
  const [completedPairs, setCompletedPairs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revision, setRevision] = useState(0);
  const [report, setReport] = useState("");
  const [query, setQuery] = useState("");
  const visibleItems = items.filter((item) => item.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    void api
      .get<Collection[]>(collectionPath)
      .then((value) => {
        if (active) setItems(value);
      })
      .catch(() => {
        if (active) setError("Could not load collections.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, revision]);
  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open && !saving) onClose();
        }}
      >
        <DialogContent className="collection-picker-dialog" closeIcon={<img src="/assets/collection-picker/close.svg" alt="" />}>
          <div className="collection-picker-header">
            <DialogHeader>
              <DialogTitle>Add to collections</DialogTitle>
              <DialogDescription>{name}</DialogDescription>
            </DialogHeader>
            <div className="collection-picker-search">
              <input aria-label="Search collections" placeholder="Search collections..." value={query} onChange={(event) => setQuery(event.target.value)} />
              <img src="/assets/collection-picker/search.svg" alt="" />
            </div>
          </div>
          {loading ? (
            <p role="status">Loading collections…</p>
          ) : error ? (
            <p role="alert">
              {error}
              <Button onClick={() => setRevision((x) => x + 1)}>
                Try again
              </Button>
            </p>
          ) : (
            <div className="collection-picker-grid">
              {items.length === 0 && (
                <p className="collection-picker-empty">No collections yet. Create one below.</p>
              )}
              {items.length > 0 && visibleItems.length === 0 && <p className="collection-picker-empty" role="status">No collections match your search.</p>}
              {visibleItems.map((item) => (
                <label className="collection-picker-item" key={item.id}>
                  <input
                    type="checkbox"
                    checked={
                      selected.includes(item.id) || done.includes(item.id)
                    }
                    disabled={saving || done.includes(item.id)}
                    onChange={(e) =>
                      setSelected((previous) =>
                        e.target.checked
                          ? [...previous, item.id]
                          : previous.filter((id) => id !== item.id)
                      )
                    }
                  />
                  <span className="collection-picker-color" aria-hidden="true" style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(item.colorLabel ?? "") ? item.colorLabel! : "var(--muted-foreground)" }} />
                  <span className="collection-picker-name">{item.name}</span>
                  <span className="collection-picker-count">
                    {done.includes(item.id)
                      ? "Added / already present"
                      : `${item.itemCount} products`}
                  </span>
                </label>
              ))}
            </div>
          )}
          {report && <p role="status">{report}</p>}
          <div className="collection-picker-footer">
            {permissions.canCreateCollections && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => setCreating(true)}
              >
                Create collection
              </Button>
            )}
            <Button
              className="collection-picker-submit"
              disabled={saving || loading || !!error || selected.length === 0}
              onClick={async () => {
                setSaving(true);
                const pairs = await Promise.all(
                  entityIds.map(async (entityId) => {
                    const pending = selected.filter(
                      (id) => !completedPairs.includes(`${id}:${entityId}`)
                    );
                    return (await addToCollections(api, entityId, pending)).map(
                      (result) => ({
                        ...result,
                        key: `${result.id}:${entityId}`,
                      })
                    );
                  })
                );
                const flattened = pairs.flat();
                setCompletedPairs((previous) => [
                  ...previous,
                  ...flattened
                    .filter((x) => x.status !== "failed")
                    .map((x) => x.key),
                ]);
                const results = selected.map((id) => ({
                  id,
                  status: flattened.some(
                    (x) => x.id === id && x.status === "failed"
                  )
                    ? "failed"
                    : flattened.some((x) => x.id === id && x.status === "added")
                    ? "added"
                    : "present",
                }));
                setDone((previous) => [
                  ...previous,
                  ...results
                    .filter((x) => x.status !== "failed")
                    .map((x) => x.id),
                ]);
                setSelected(
                  results.filter((x) => x.status === "failed").map((x) => x.id)
                );
                setReport(
                  `${
                    flattened.filter((x) => x.status === "added").length
                  } added; ${
                    flattened.filter((x) => x.status === "present").length
                  } already present; ${
                    flattened.filter((x) => x.status === "failed").length
                  } failed.${
                    flattened.some((x) => x.status === "failed")
                      ? " Retry the failed selections."
                      : ""
                  }`
                );
                setSaving(false);
                if (results.every((result) => result.status !== "failed")) {
                  onClose();
                  return;
                }
                setRevision((x) => x + 1);
              }}
            >
              {saving ? "Adding…" : "Add selected"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {creating && (
        <CollectionEditor
          onClose={() => setCreating(false)}
          onSaved={(value) => {
            setCreating(false);
            setItems((previous) => [...previous, value]);
            setSelected((previous) => [...previous, value.id]);
          }}
        />
      )}
    </>
  );
}
